/* Mr Pixel v2 home behaviour: hero slideshow, Shop by Platform tabs, scrolling product rows.
 * Each part binds to its own data- hook and does nothing if the hook is absent. Runs again on the
 * theme editor's section load so the customizer preview stays live. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function hero(root) {
    if (root.dataset.bound) return;
    root.dataset.bound = '1';
    const slides = [...root.querySelectorAll('[data-slide]')];
    const tabs = [...root.querySelectorAll('[data-tab]')];
    const pause = root.querySelector('[data-pause]');
    const n = slides.length;
    if (n < 2) return;
    const ms = (parseInt(root.dataset.interval, 10) || 6) * 1000;
    let i = 0, timer = null, playing = !reduce.matches, started = 0, left = ms;

    // The tab's progress bar grows by transform, not width: a width transition re-runs layout on every
    // frame for the whole slide, which cost seconds of main thread on a throttled phone (pre-publish Lighthouse).
    function bar(k, run) {
      const b = tabs[k] && tabs[k].querySelector('.bar i');
      if (!b) return;
      // round 22 (client): the header's four colour blocks, uncovered by a clip, so they never stretch.
      // Round 23 (Jordan): a smooth, continuous fill again (linear), so each colour fills in turn.
      b.style.transition = 'none';
      b.style.clipPath = 'inset(0 100% 0 0)';
      if (run) {
        void b.offsetWidth;
        b.style.transition = `clip-path ${left}ms linear`;
        b.style.clipPath = 'inset(0 0 0 0)';
      }
    }
    // Position on the stage: -1 left peek, 0 centre, 1 right peek, null hidden.
    const place = (rel) => (rel === 0 ? 0 : rel === 1 ? 1 : rel === n - 1 && n > 2 ? -1 : null);
    function show(k) {
      // polish H-03: text choreography runs from the first change on, never on first paint (LCP)
      if (root.dataset.shown) root.classList.add('mp2-moved');
      root.dataset.shown = '1';
      i = (k + n) % n;
      slides.forEach((s, j) => {
        const rel = (j - i + n) % n;
        const was = s.dataset.pos === undefined ? null : (s.dataset.pos === '' ? null : +s.dataset.pos);
        const now = place(rel);
        s.dataset.pos = now === null ? '' : now;
        // A slide that wraps from one peek to the other (or appears from hidden) would otherwise
        // animate straight across the stage (client meeting 2026-10-04). It jumps there unseen
        // and fades in instead.
        const jump = s.dataset.placed === '1' && now !== null && (was === null || Math.abs(now - was) > 1);
        s.dataset.placed = '1';
        if (jump) { s.style.transition = 'none'; s.style.opacity = '0'; }
        s.classList.toggle('is-active', rel === 0);
        s.classList.toggle('is-next', rel === 1);
        s.classList.toggle('is-prev', rel === n - 1 && n > 2);
        s.classList.toggle('mp2-off', rel !== 0 && rel !== 1 && rel !== n - 1);
        s.setAttribute('aria-hidden', rel === 0 ? 'false' : 'true');
        s.tabIndex = rel === 0 ? 0 : -1;
        if (jump) { void s.offsetWidth; s.style.transition = ''; s.style.opacity = ''; }
      });
      tabs.forEach((t, j) => {
        t.classList.toggle('on', j === i);
        t.setAttribute('aria-current', j === i ? 'true' : 'false');
        bar(j, false);
      });
      left = ms;
      if (playing) run();
    }
    function run() {
      clearTimeout(timer);
      if (held) { bar(i, false); return; }
      started = Date.now();
      bar(i, true);
      timer = setTimeout(() => show(i + 1), left);
    }
    function freeze() {
      clearTimeout(timer);
      left = Math.max(400, left - (Date.now() - started));
      const b = tabs[i] && tabs[i].querySelector('.bar i');
      if (b) { b.style.clipPath = getComputedStyle(b).clipPath; b.style.transition = 'none'; }
    }
    function setPlaying(p) {
      playing = p;
      if (pause) {
        pause.setAttribute('aria-label', p ? 'Pause slideshow' : 'Play slideshow');
        pause.querySelector('use').setAttribute('href', p ? '#mp2-pause' : '#mp2-play');
      }
      if (p) run();
      else freeze();
    }
    // polish H-03: a pointer resting on the slideshow holds it (the pause button keeps its own state)
    let held = false;
    root.addEventListener('mouseenter', () => { if (playing && !held) { held = true; freeze(); } });
    root.addEventListener('mouseleave', () => { if (held) { held = false; if (playing) run(); } });
    root.querySelector('[data-prev]')?.addEventListener('click', () => show(i - 1));
    root.querySelector('[data-next]')?.addEventListener('click', () => show(i + 1));
    tabs.forEach((t) => t.addEventListener('click', () => show(+t.dataset.tab)));
    pause?.addEventListener('click', () => setPlaying(!playing));
    // a click on a peeking slide brings it forward instead of following its link
    slides.forEach((s, j) => s.addEventListener('click', (e) => {
      if (j !== i) { e.preventDefault(); show(j); }
    }));
    let x0 = null;
    root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) show(i + (dx < 0 ? 1 : -1));
      x0 = null;
    });
    root.addEventListener('focusin', () => { if (playing) setPlaying(false); root.dataset.focusPaused = '1'; });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { clearTimeout(timer); } else if (playing) run();
    });
    if (!playing && pause) setPlaying(false);
    show(0);
  }

  function platform(root) {
    if (root.dataset.bound) return;
    root.dataset.bound = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    // polish H-05: one indicator slides between the tabs instead of each tab filling in place
    const list = root.querySelector('.famtabs');
    let ind = null, cur = null;
    if (list && tabs.length > 1) {
      ind = document.createElement('span');
      ind.className = 'mp2-famind is-instant';
      ind.setAttribute('aria-hidden', 'true');
      list.prepend(ind);
      list.classList.add('has-ind');
    }
    function place(instant) {
      if (!ind || !cur) return;
      ind.classList.toggle('is-instant', !!instant);
      ind.style.width = cur.offsetWidth + 'px';
      ind.style.height = cur.offsetHeight + 'px';
      ind.style.transform = `translate(${cur.offsetLeft}px, ${cur.offsetTop}px)`;
      ind.style.backgroundColor = `var(--fam-${cur.dataset.fam})`;
    }
    if (ind && 'ResizeObserver' in window) new ResizeObserver(() => place(true)).observe(list);
    function pick(t, focus) {
      const first = !cur;
      tabs.forEach((x) => {
        const on = x === t;
        x.classList.toggle('on', on);
        x.setAttribute('aria-selected', on ? 'true' : 'false');
        x.tabIndex = on ? 0 : -1;
        const p = document.getElementById(x.getAttribute('aria-controls'));
        // Panels share one cell (.pstage) and cross-fade, so they stay laid out: `hidden` (the
        // no-JS state) comes off and `inert` keeps the faded ones out of tab order and AT.
        if (p) {
          p.hidden = false; p.classList.toggle('is-on', on); p.inert = !on;
          // polish H-05: the incoming tiles arrive as a set (not on first load)
          if (on && !first && cur !== t) {
            p.classList.remove('mp2-swap'); void p.offsetWidth; p.classList.add('mp2-swap');
            clearTimeout(p._mp2Swap); p._mp2Swap = setTimeout(() => p.classList.remove('mp2-swap'), 800);
          }
        }
      });
      cur = t;
      place(first);
      root.style.setProperty('--famc', `var(--fam-${t.dataset.fam})`);
      if (focus) t.focus();
    }
    tabs.forEach((t, k) => {
      t.addEventListener('click', () => pick(t));
      t.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (d) { e.preventDefault(); pick(tabs[(k + d + tabs.length) % tabs.length], true); }
      });
    });
    if (tabs[0]) pick(tabs[0]);
  }

  function row(root) {
    if (root.dataset.bound) return;
    root.dataset.bound = '1';
    const track = root.querySelector('[data-track]');
    const thumb = root.querySelector('[data-thumb]');
    const prev = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (!track) return;
    function update() {
      const max = track.scrollWidth - track.clientWidth;
      const frac = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      if (thumb) {
        thumb.style.width = `${Math.max(12, frac * 100)}%`;
        thumb.style.marginLeft = `${max > 0 ? (track.scrollLeft / max) * (100 - frac * 100) : 0}%`;
      }
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    }
    const step = () => {
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card ? (card.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(track.clientWidth / (card.getBoundingClientRect().width + gap))) : track.clientWidth;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduce.matches ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduce.matches ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // Reviews as the design's bubbles (client meeting 2026-10-04). Judge.me's carousel loads as the
  // source; its cards carry the text and the lit stars. The carousel clones cards to loop, so
  // reviews are de-duplicated by data-review-id. 3 a page on a desktop, 2 on a tablet, 1 on a phone
  // (the widths home.css already uses for .rgrid).
  function reviews(own) {
    if (own.dataset.bound) return;
    own.dataset.bound = '1';
    const col = own.closest('.mp2-rev-col');
    const src = col && col.querySelector('.mp2-rev-app');
    const grid = own.querySelector('.rgrid');
    const dots = own.querySelector('.dots');
    const tpl = own.querySelector('template[data-bubble]');
    if (!src || !grid || !tpl) return;
    const wide = window.matchMedia('(min-width: 1024px)');
    const mid = window.matchMedia('(min-width: 561px)');
    let list = [], page = 0;
    const per = () => (wide.matches ? 3 : mid.matches ? 2 : 1);

    function read() {
      const seen = new Set(), out = [];
      src.querySelectorAll('.jdgm-card').forEach((c) => {
        const id = c.dataset.reviewId || c.textContent;
        const text = (c.querySelector('.jdgm-text') || {}).textContent;
        if (!text || !text.trim() || seen.has(id)) return;
        seen.add(id);
        out.push({ text: text.trim(), stars: c.querySelectorAll('.jdgm-star.jdgm--on').length || 5 });
      });
      return out;
    }
    // Client meeting 2026-10-04: pages slide/fade in, and the grid is held at the tallest page's
    // height (measured once per breakpoint), so a long review never makes the section grow.
    function fill() {
      const n = per();
      grid.replaceChildren(...list.slice(page * n, page * n + n).map((r) => {
        const f = tpl.content.firstElementChild.cloneNode(true);
        f.querySelector('p').textContent = r.text;
        const s = f.querySelector('.stars');
        s.setAttribute('aria-label', `${r.stars} out of 5 stars`);
        s.innerHTML = '<svg class="i" aria-hidden="true"><use href="#mp2-star"/></svg>'.repeat(r.stars);
        return f;
      }));
    }
    let measuredFor = 0;
    function measure() {
      const n = per(), pages = Math.max(1, Math.ceil(list.length / n)), keep = page;
      grid.style.minHeight = '';
      let max = 0;
      for (page = 0; page < pages; page += 1) { fill(); max = Math.max(max, grid.offsetHeight); }
      page = keep;
      grid.style.minHeight = max + 'px';
      measuredFor = n;
    }
    function draw(dir) {
      const n = per(), pages = Math.max(1, Math.ceil(list.length / n));
      page = (page + pages) % pages;
      if (measuredFor !== n) measure();
      fill();
      if (dir && !reduce.matches && grid.animate) {
        grid.animate([{ opacity: 0, transform: `translateX(${dir * 24}px)` }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
      dots.replaceChildren(...Array.from({ length: pages }, (_, k) => {
        const d = document.createElement('i');
        if (k === page) d.className = 'on';
        return d;
      }));
      own.querySelector('.rev-ctrl').hidden = pages < 2;
    }
    function tryBuild() {
      const got = read();
      if (!got.length || got.length === list.length) return got.length > 0;
      list = got;
      measuredFor = 0;
      own.hidden = false;
      src.classList.add('mp2-rev-src');
      draw();
      return true;
    }
    own.querySelector('[data-prev]')?.addEventListener('click', () => { page -= 1; draw(-1); });
    own.querySelector('[data-next]')?.addEventListener('click', () => { page += 1; draw(1); });
    // Round 21 (client 2026-10-06): swipe the reviews with a finger, or drag them with a mouse. Horizontal moves only
    // (touch-action: pan-y in CSS keeps the page scrolling); the cards follow the pointer a little, then page.
    let sx = null, sy = 0, sid = null;
    grid.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || list.length <= per()) return;
      sx = e.clientX; sy = e.clientY; sid = e.pointerId;
    });
    grid.addEventListener('pointermove', (e) => {
      if (sx === null || e.pointerId !== sid) return;
      const dx = e.clientX - sx;
      if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(e.clientY - sy)) { grid.classList.add('is-dragging'); grid.style.transform = `translateX(${dx * 0.35}px)`; }
    });
    const end = (e) => {
      if (sx === null || e.pointerId !== sid) return;
      const dx = e.clientX - sx, dy = e.clientY - sy;
      sx = null; grid.classList.remove('is-dragging'); grid.style.transform = '';
      if (e.type === 'pointerup' && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) { page += dx < 0 ? 1 : -1; draw(dx < 0 ? 1 : -1); }
    };
    grid.addEventListener('pointerup', end);
    grid.addEventListener('pointercancel', end);
    grid.addEventListener('pointerleave', end);
    [wide, mid].forEach((m) => m.addEventListener('change', () => { if (list.length) { page = 0; measuredFor = 0; draw(); } }));
    // Judge.me draws its carousel after load, in steps: rebuild once it has been quiet for half a
    // second (only if the review count changed), and stop watching after 20 s.
    tryBuild();
    let quiet = null;
    const mo = new MutationObserver(() => { clearTimeout(quiet); quiet = setTimeout(tryBuild, 500); });
    mo.observe(src, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 20000);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-mp2-rev]').forEach(reviews);
    (scope || document).querySelectorAll('[data-mp2-hero]').forEach(hero);
    (scope || document).querySelectorAll('[data-mp2-plat]').forEach(platform);
    (scope || document).querySelectorAll('[data-mp2-row]').forEach(row);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* Round 9 (Jordan 2026-10-04): collection "Load more" (main-collection-product-grid patch). Fetches the next
   page, appends its cards to the grid and swaps in that page's "Showing X of Y" (counted server-side, so it is
   cumulative). Delegated, so it survives the filter form re-rendering the grid. Without JS it is a plain link. */
(() => {
  document.addEventListener('click', async (e) => {
    const a = e.target.closest('[data-mp2-more]');
    if (!a) return;
    // The list itself: on a collection #product-grid is the <ul>; on search (2026-10-05) it wraps one.
    const listIn = (root) => root.querySelector('ul#product-grid, #product-grid ul.product-grid');
    const grid = listIn(document);
    const wrap = a.closest('[data-mp2-more-wrap]');
    if (!grid || !wrap) return;
    e.preventDefault();
    if (a.getAttribute('aria-busy') === 'true') return;
    // polish C-07: the four-diamond loader in the button (mp2-polish.css), its width held
    a.setAttribute('aria-busy', 'true');
    try {
      const html = await (await fetch(a.href, { credentials: 'same-origin' })).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextList = listIn(doc);
      const items = nextList ? nextList.querySelectorAll(':scope > li') : [];
      const firstNew = items[0];
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // the first eight new cards rise in, 30ms apart; the rest simply appear
      items.forEach((li, k) => {
        const node = document.importNode(li, true);
        if (k < 8 && !still) { node.classList.add('mp2-new'); node.style.setProperty('--i', k); }
        grid.appendChild(node);
      });
      const oldBar = wrap.querySelector('.mp2-more__bar i');
      const oldW = oldBar && oldBar.style.width;
      const next = doc.querySelector('[data-mp2-more-wrap]');
      if (next) {
        const fresh = document.importNode(next, true);
        wrap.replaceWith(fresh);
        // the progress bar grows from where it was
        const bar = fresh.querySelector('.mp2-more__bar i');
        if (bar && oldW && !still) {
          const w = bar.style.width; bar.style.width = oldW;
          requestAnimationFrame(() => requestAnimationFrame(() => { bar.style.width = w; }));
        }
      } else wrap.remove();
      const focusTo = firstNew && grid.querySelector(`li:nth-child(${grid.children.length - items.length + 1}) a`);
      if (focusTo) focusTo.focus({ preventScroll: true });
    } catch (err) {
      window.location.href = a.href;
    }
  });
})();
